import { useEffect, useMemo, useState } from "react";
import AuditorLayout from "@/components/AuditorLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileText, Search, Download, ExternalLink, Folder, FolderOpen,
  ChevronRight, Loader2, FileSpreadsheet, FileType2, Image as ImageIcon,
  Star, Clock, X, Maximize2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

type Doc = {
  id: string; name: string; description: string | null;
  control_number: string | null; framework: string | null; framework_ref: string | null;
  category: string | null; vessel_name: string | null;
  revision_number: string | null; version: string | null;
  revision_date: string | null;
  next_review_date: string | null; doc_status: string | null;
  document_owner: string | null; approved_by: string | null;
  file_path: string | null; is_archived: boolean;
};

const FOLDERS = [
  "Class and Flag", "Corporate Governance", "DP Governance", "General Ops",
  "HR", "Operational Excellence", "QMS", "SMS", "Supply Chain", "Vessel Checklist",
];

const INLINE_EXT = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "svg", "txt"];
const OFFICE_EXT = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
const ext = (name: string) => name.split(".").pop()?.toLowerCase() || "";

const fileIcon = (name: string) => {
  const e = ext(name);
  if (["xls", "xlsx", "csv"].includes(e)) return <FileSpreadsheet className="w-4 h-4 text-emerald-600" />;
  if (["doc", "docx"].includes(e)) return <FileType2 className="w-4 h-4 text-blue-600" />;
  if (["ppt", "pptx"].includes(e)) return <FileType2 className="w-4 h-4 text-orange-600" />;
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(e)) return <ImageIcon className="w-4 h-4 text-violet-600" />;
  if (e === "pdf") return <FileText className="w-4 h-4 text-rose-600" />;
  return <FileText className="w-4 h-4 text-slate-500" />;
};

const StatusBadge = ({ status }: { status: string | null }) => {
  const s = (status || "current").toLowerCase();
  const cls = s === "current"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : s === "draft"
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-100 text-slate-600 border-slate-200";
  return <Badge variant="outline" className={`${cls} text-[10px] capitalize`}>{s}</Badge>;
};

type ViewerEngine = "google" | "office";

type Selected = {
  doc: Doc;
  url: string;
  kind: "pdf" | "image" | "office" | "text" | "other";
  engine: ViewerEngine; // for office docs
  attempt: number; // forces iframe remount
};

const RECENTS_KEY = "auditor-recent-docs";
const FAVS_KEY = "auditor-fav-docs";
const PREVIEW_TIMEOUT_MS = 12000;

const AuditorLibrary = () => {
  const [search, setSearch] = useState("");
  const [activeFolder, setActiveFolder] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selected, setSelected] = useState<Selected | null>(null);
  const [loadingDoc, setLoadingDoc] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewStuck, setPreviewStuck] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [recents, setRecents] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]"); } catch { return []; }
  });
  const [favs, setFavs] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(FAVS_KEY) || "[]"); } catch { return []; }
  });

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["auditor-library-docs"],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("operating_documents")
        .select("*")
        .eq("is_archived", false)
        .order("category").order("name");
      return (data as Doc[]) ?? [];
    },
  });

  const auditableDocs = useMemo(
    () => docs.filter((d) => (d.category || "").toLowerCase() !== "security"),
    [docs],
  );

  const folderCounts = useMemo(() => {
    const c: Record<string, number> = { all: auditableDocs.length };
    FOLDERS.forEach((f) => (c[f] = 0));
    auditableDocs.forEach((d) => {
      const m = FOLDERS.find((f) => f.toLowerCase() === (d.category || "").toLowerCase());
      if (m) c[m]++;
    });
    return c;
  }, [auditableDocs]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return auditableDocs
      .filter((d) => activeFolder === "all" || (d.category || "").toLowerCase() === activeFolder.toLowerCase())
      .filter((d) => statusFilter === "all" || (d.doc_status || "current").toLowerCase() === statusFilter)
      .filter((d) => !s || [d.name, d.control_number, d.framework_ref, d.description, d.document_owner]
        .filter(Boolean).some((v) => (v as string).toLowerCase().includes(s)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [auditableDocs, activeFolder, statusFilter, search]);

  const recentDocs = useMemo(
    () => recents.map((id) => auditableDocs.find((d) => d.id === id)).filter(Boolean) as Doc[],
    [recents, auditableDocs],
  );
  const favDocs = useMemo(
    () => favs.map((id) => auditableDocs.find((d) => d.id === id)).filter(Boolean) as Doc[],
    [favs, auditableDocs],
  );

  const toggleFav = (id: string) => {
    setFavs((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev].slice(0, 50);
      localStorage.setItem(FAVS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const pushRecent = (id: string) => {
    setRecents((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, 8);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const selectDoc = async (doc: Doc) => {
    if (!doc.file_path) { toast.error("No file attached to this document."); return; }
    if (loadingDoc === doc.id) return;
    setLoadingDoc(doc.id);
    setPreviewLoading(true);
    setPreviewStuck(false);
    try {
      const { data, error } = await supabase.storage
        .from("operating-documents")
        .createSignedUrl(doc.file_path, 3600);
      if (error || !data?.signedUrl) {
        toast.error("Unable to load this document. Please try again.");
        return;
      }
      const e = ext(doc.file_path);
      const kind: Selected["kind"] =
        e === "pdf" ? "pdf"
        : ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(e) ? "image"
        : OFFICE_EXT.includes(e) ? "office"
        : e === "txt" ? "text"
        : "other";
      setSelected({ doc, url: data.signedUrl, kind, engine: "google", attempt: 0 });
      pushRecent(doc.id);
    } finally {
      setLoadingDoc(null);
    }
  };

  // Keyboard navigation (↑/↓ or j/k to move through the list)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!selected || (e.target as HTMLElement)?.tagName === "INPUT") return;
      const idx = filtered.findIndex((d) => d.id === selected.doc.id);
      if (idx === -1) return;
      if (e.key === "ArrowDown" || e.key === "j") {
        const next = filtered[Math.min(filtered.length - 1, idx + 1)];
        if (next && next.id !== selected.doc.id) { e.preventDefault(); selectDoc(next); }
      } else if (e.key === "ArrowUp" || e.key === "k") {
        const prev = filtered[Math.max(0, idx - 1)];
        if (prev && prev.id !== selected.doc.id) { e.preventDefault(); selectDoc(prev); }
      } else if (e.key === "Escape" && fullscreen) {
        setFullscreen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, filtered, fullscreen]);

  // Auto-select the first document so the preview pane is never empty.
  useEffect(() => {
    if (!selected && filtered.length > 0 && !isLoading) {
      const firstWithFile = filtered.find((d) => d.file_path);
      if (firstWithFile) selectDoc(firstWithFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, filtered.length]);

  // Watchdog: if preview hasn't loaded within ~12s, surface fallback options.
  useEffect(() => {
    if (!previewLoading) { setPreviewStuck(false); return; }
    const t = window.setTimeout(() => setPreviewStuck(true), PREVIEW_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [previewLoading, selected?.url, selected?.engine, selected?.attempt]);

  const switchEngine = () => {
    if (!selected) return;
    const nextEngine: ViewerEngine = selected.engine === "google" ? "office" : "google";
    setPreviewLoading(true);
    setPreviewStuck(false);
    setSelected({ ...selected, engine: nextEngine, attempt: selected.attempt + 1 });
  };

  const reloadPreview = () => {
    if (!selected) return;
    setPreviewLoading(true);
    setPreviewStuck(false);
    setSelected({ ...selected, attempt: selected.attempt + 1 });
  };

  const previewSrc = useMemo(() => {
    if (!selected) return "";
    if (selected.kind === "office") {
      // Google Docs Viewer is more reliable for .docx than Office Online,
      // especially with private bucket signed URLs.
      return selected.engine === "office"
        ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(selected.url)}`
        : `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(selected.url)}`;
    }
    if (selected.kind === "pdf") return `${selected.url}#toolbar=1&navpanes=0&view=FitH`;
    return selected.url;
  }, [selected]);

  const renderPreview = () => {
    if (!selected) {
      return (
        <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
          <FileText className="w-12 h-12 opacity-30" />
          <p className="text-sm">Select a document to preview.</p>
        </div>
      );
    }
    if (selected.kind === "image") {
      return (
        <div className="h-full overflow-auto bg-slate-100 flex items-center justify-center p-6">
          <img src={selected.url} alt={selected.doc.name} className="max-w-full max-h-full object-contain shadow-lg" />
        </div>
      );
    }
    return (
      <div className="relative h-full bg-slate-100">
        {previewLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50/90 z-10 gap-3 px-6 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-teal" />
            <p className="text-xs text-slate-500">
              {selected.kind === "office"
                ? `Rendering with ${selected.engine === "google" ? "Google" : "Microsoft"} viewer…`
                : "Loading preview…"}
            </p>
            {previewStuck && (
              <div className="flex flex-col items-center gap-2 mt-1">
                <p className="text-[11px] text-slate-500 max-w-xs">
                  This is taking longer than usual. Try an alternate viewer or open the file directly.
                </p>
                <div className="flex gap-2">
                  {selected.kind === "office" && (
                    <Button size="sm" variant="outline" onClick={switchEngine}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                      Try {selected.engine === "google" ? "Microsoft" : "Google"} viewer
                    </Button>
                  )}
                  <Button size="sm" variant="outline" asChild>
                    <a href={selected.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in new tab
                    </a>
                  </Button>
                  <Button size="sm" variant="default" asChild className="bg-teal hover:bg-teal/90 text-white">
                    <a href={selected.url} download={selected.doc.name}>
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        <iframe
          key={`${selected.url}-${selected.engine}-${selected.attempt}`}
          src={previewSrc}
          title={selected.doc.name}
          className="w-full h-full border-0"
          onLoad={() => setPreviewLoading(false)}
        />
      </div>
    );
  };

  const PreviewToolbar = () => {
    if (!selected) return null;
    const isFav = favs.includes(selected.doc.id);
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {fileIcon(selected.doc.name)}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{selected.doc.name}</div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              {selected.doc.control_number && <span className="font-mono">{selected.doc.control_number}</span>}
              {selected.doc.revision_number && <span>· Rev {selected.doc.revision_number}</span>}
              <StatusBadge status={selected.doc.doc_status} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" onClick={() => toggleFav(selected.doc.id)} title={isFav ? "Remove favorite" : "Add favorite"}>
            <Star className={`w-4 h-4 ${isFav ? "fill-amber-400 text-amber-400" : "text-slate-400"}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={reloadPreview} title="Reload preview">
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </Button>
          {selected.kind === "office" && (
            <Button size="sm" variant="ghost" onClick={switchEngine} title={`Switch to ${selected.engine === "google" ? "Microsoft" : "Google"} viewer`}>
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 px-1">
                {selected.engine === "google" ? "G" : "MS"}
              </span>
            </Button>
          )}
          <Button size="sm" variant="outline" asChild>
            <a href={selected.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open
            </a>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a href={selected.url} download={selected.doc.name}>
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download
            </a>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setFullscreen((v) => !v)} title="Fullscreen">
            <Maximize2 className="w-4 h-4 text-slate-500" />
          </Button>
        </div>
      </div>
    );
  };

  const DocListItem = ({ d }: { d: Doc }) => {
    const active = selected?.doc.id === d.id;
    const loading = loadingDoc === d.id;
    return (
      <button
        onClick={() => selectDoc(d)}
        className={`w-full text-left flex items-start gap-2.5 px-3 py-2.5 rounded-lg border transition-all ${
          active
            ? "bg-teal/10 border-teal/40 shadow-sm"
            : "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300"
        }`}
      >
        <div className="mt-0.5 shrink-0">{loading ? <Loader2 className="w-4 h-4 animate-spin text-teal" /> : fileIcon(d.name)}</div>
        <div className="min-w-0 flex-1">
          <div className={`text-sm truncate ${active ? "text-teal font-semibold" : "text-slate-900 font-medium"}`}>{d.name}</div>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-[11px] text-slate-500">
            {d.control_number && <span className="font-mono">{d.control_number}</span>}
            {d.revision_number && <span>Rev {d.revision_number}</span>}
            {d.framework_ref && <span className="text-teal">{d.framework_ref}</span>}
          </div>
        </div>
        {favs.includes(d.id) && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 shrink-0 mt-1" />}
      </button>
    );
  };

  return (
    <AuditorLayout>
      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="text-[10px] uppercase tracking-[3px] font-semibold text-teal">Velocity Offshore · Audit Library</span>
          <h1 className="text-2xl lg:text-3xl font-display font-semibold text-slate-900 leading-tight mt-1">Documents &amp; SOPs</h1>
          <p className="text-xs text-slate-500 mt-1.5 max-w-2xl">
            Browse the controlled document library. Click any document to preview inline — download or open in a new tab anytime.
          </p>
        </div>
        <Button asChild size="sm" variant="outline" className="border-slate-300 shrink-0">
          <a href="/auditor/VOSM_Master_Document_Register_Rev1.xlsx" download>
            <Download className="w-4 h-4 mr-1.5" /> Master Register (XLSX)
          </a>
        </Button>
      </div>

      {/* Split-view portal */}
      <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)] min-h-[600px]">
        {/* Left: folder tree + recents/favorites */}
        <aside className="col-span-12 lg:col-span-3 xl:col-span-2 bg-white border border-slate-200 rounded-xl p-3 overflow-y-auto">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 mb-1.5">Folders</p>
          <div className="space-y-0.5 mb-4">
            <button
              onClick={() => setActiveFolder("all")}
              className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                activeFolder === "all" ? "bg-teal/10 text-teal font-semibold" : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              <span className="flex items-center gap-2"><FolderOpen className="w-4 h-4" /> All Documents</span>
              <span className="text-[10px] text-slate-400">{folderCounts.all}</span>
            </button>
            {FOLDERS.map((f) => {
              const active = activeFolder === f;
              return (
                <button
                  key={f}
                  onClick={() => setActiveFolder(f)}
                  className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                    active ? "bg-teal/10 text-teal font-semibold" : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {active ? <FolderOpen className="w-4 h-4 shrink-0" /> : <Folder className="w-4 h-4 shrink-0" />}
                    <span className="truncate">{f}</span>
                  </span>
                  <span className="text-[10px] text-slate-400 shrink-0">{folderCounts[f] || 0}</span>
                </button>
              );
            })}
          </div>

          {favDocs.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 mb-1.5 flex items-center gap-1">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Favorites
              </p>
              <div className="space-y-0.5 mb-4">
                {favDocs.slice(0, 6).map((d) => (
                  <button
                    key={d.id}
                    onClick={() => selectDoc(d)}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-slate-600 hover:bg-slate-100 truncate"
                  >
                    {fileIcon(d.name)}<span className="truncate">{d.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {recentDocs.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-2 mb-1.5 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Recent
              </p>
              <div className="space-y-0.5">
                {recentDocs.slice(0, 5).map((d) => (
                  <button
                    key={d.id}
                    onClick={() => selectDoc(d)}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-slate-600 hover:bg-slate-100 truncate"
                  >
                    {fileIcon(d.name)}<span className="truncate">{d.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </aside>

        {/* Middle: document list */}
        <section className="col-span-12 lg:col-span-4 xl:col-span-3 bg-white border border-slate-200 rounded-xl flex flex-col overflow-hidden">
          <div className="p-3 border-b border-slate-200 space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search documents…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 bg-white border-slate-200 text-sm"
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs bg-white border-slate-200 flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="current">Current</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="superseded">Superseded</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[10px] text-slate-500 shrink-0">{filtered.length} doc{filtered.length === 1 ? "" : "s"}</span>
            </div>
            {activeFolder !== "all" && (
              <div className="flex items-center gap-1 text-[11px] text-slate-500">
                <span>All</span><ChevronRight className="w-3 h-3" />
                <span className="text-slate-900 font-medium">{activeFolder}</span>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {isLoading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-teal" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-10">No documents match.</p>
            ) : (
              filtered.map((d) => <DocListItem key={d.id} d={d} />)
            )}
          </div>
        </section>

        {/* Right: preview pane */}
        <section className="col-span-12 lg:col-span-5 xl:col-span-7 bg-white border border-slate-200 rounded-xl flex flex-col overflow-hidden">
          <PreviewToolbar />
          <div className="flex-1 overflow-hidden">{renderPreview()}</div>
          {selected && (
            <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
              {selected.doc.framework_ref && <span><span className="text-slate-400">Framework:</span> <span className="text-teal">{selected.doc.framework_ref}</span></span>}
              {selected.doc.revision_date && <span><span className="text-slate-400">Revised:</span> {selected.doc.revision_date}</span>}
              {selected.doc.next_review_date && <span><span className="text-slate-400">Next review:</span> {selected.doc.next_review_date}</span>}
              {selected.doc.document_owner && <span><span className="text-slate-400">Owner:</span> {selected.doc.document_owner}</span>}
              {selected.doc.category && <span><span className="text-slate-400">Folder:</span> {selected.doc.category}</span>}
            </div>
          )}
        </section>
      </div>

      {/* Fullscreen preview overlay */}
      {fullscreen && selected && (
        <div className="fixed inset-0 z-50 bg-slate-900/95 flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 bg-white">
            <div className="flex items-center gap-2 min-w-0">
              {fileIcon(selected.doc.name)}
              <span className="text-sm font-semibold text-slate-900 truncate">{selected.doc.name}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="outline" asChild>
                <a href={selected.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open</a>
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a href={selected.url} download={selected.doc.name}><Download className="w-3.5 h-3.5 mr-1.5" />Download</a>
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setFullscreen(false)}><X className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="flex-1 bg-slate-100">{renderPreview()}</div>
        </div>
      )}
    </AuditorLayout>
  );
};

export default AuditorLibrary;
